// ============================================================================
// The same three export buttons, in the same order, on every report screen.
//
// ⛔ Ben, relaying HR (2026-09-22): "Excel and PDF and CSV (in that order,
// with Excel as default)." Default here means VISUALLY PRIMARY, not
// pre-selected — nothing downloads until somebody presses something.
//
// One component so the order can never drift between screens, which is
// exactly how "the pay sheet has CSV first but the summary has Excel first"
// happens and then nobody trusts any of them.
// ============================================================================

import { useState } from 'react';
import { Button } from '../../shared/components';
import { downloadCsv, downloadExcel, printToPdf, type CellValue } from './export-file';

export interface ExportButtonsProps {
  /** Without the extension — this adds .xlsx / .csv. */
  baseFilename: string;
  /** The sheet's name inside the workbook. */
  sheetName: string;
  /** Header row first. Built ONCE and used by both file formats. */
  rows: () => CellValue[][];
  disabled?: boolean;
  /** Extra label after the buttons, e.g. what the PDF will contain. */
  hint?: string;
  /** Excel number format for the number cells (e.g. '0.000'); omitted = General. */
  numberFormat?: string;
}

export function ExportButtons({ baseFilename, sheetName, rows, disabled, hint, numberFormat }: ExportButtonsProps) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  async function excel() {
    setBusy(true);
    setFailed(null);
    try {
      await downloadExcel(`${baseFilename}.xlsx`, sheetName, rows(), numberFormat);
    } catch {
      // ⛔ Say so rather than appearing to do nothing. A download that fails
      // silently is indistinguishable from a browser that blocked it.
      setFailed('Excel would not build. Try CSV, which needs nothing from the browser.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="no-print flex flex-wrap items-center gap-2">
      <Button size="sm" loading={busy} disabled={disabled} onClick={() => void excel()}>
        Excel
      </Button>
      <Button size="sm" variant="secondary" disabled={disabled} onClick={printToPdf}>
        PDF
      </Button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => downloadCsv(`${baseFilename}.csv`, rows())}
        className="rounded-lg px-2 py-1.5 text-xs font-semibold text-tcs-text-muted underline hover:text-tcs-text disabled:opacity-50"
      >
        CSV
      </button>
      {hint && <span className="text-[11px] text-tcs-text-muted">{hint}</span>}
      {failed && <span className="text-[11px] text-st-danger-fg">{failed}</span>}
    </div>
  );
}
